<template>
  <div class="cost-submission">
    <div class="container">
      <div class="header">
        <h1>Cost & Expense Management</h1>
        <p>View and manage cost submissions for branches</p>
      </div>

      <!-- Add Tab Navigation -->
      <div class="tab-navigation">
        <button 
          class="tab-button" 
          :class="{ active: activeTab === 'submit' }"
          @click="activeTab = 'submit'"
        >
          <i class="fa-solid fa-plus"></i>
          Submit Costs
        </button>
        <button 
          class="tab-button" 
          :class="{ active: activeTab === 'bulk' }"
          @click="activeTab = 'bulk'"
        >
          <i class="fa-solid fa-table"></i>
          Bulk Entry
        </button>
        <button 
          class="tab-button" 
          :class="{ active: activeTab === 'manage' }"
          @click="activeTab = 'manage'"
        >
          <i class="fa-solid fa-list"></i>
          Manage Costs
        </button>
      </div>

      <!-- Manage Costs Tab -->
      <div v-if="activeTab === 'manage'" class="tab-content">
        <div class="filter-description">
          <p>
            <i class="fa-solid fa-circle-info"></i>
            Select a branch to view its cost records, or use the month/year filters to analyze specific periods.
          </p>
        </div>

        <div class="records-container">
          <div class="card-header">
            <div class="header-icon">
              <i class="fa-solid fa-building"></i>
            </div>
            <h2>Cost Records</h2>
          </div>

          <!-- Filters -->
          <div class="filters">
            <div class="filter-group">
              <div class="subsidiary-selector">
                <div class="selected-subsidiary" @click="toggleSubsidiaryDropdown">
                  <span>{{ selectedSubsidiaryName }}</span>
                  <i class="fa-solid fa-chevron-down" :class="{ 'rotate': showSubsidiaryDropdown }"></i>
                </div>
                <div class="subsidiary-dropdown" v-if="showSubsidiaryDropdown">
                  <div class="search-box">
                    <input 
                      type="text" 
                      v-model="subsidiarySearch" 
                      placeholder="Search subsidiaries..."
                      @click.stop
                    >
                  </div>
                  <div 
                    v-for="sub in filteredSubsidiaries" 
                    :key="sub.id" 
                    class="subsidiary-option"
                    :class="{ 'active': selectedSubsidiary === sub.id }"
                    @click="selectSubsidiary(sub)"
                  >
                    {{ sub.name }}
                  </div>
                </div>
              </div>

              <div class="month-year-select">
                <select v-model="selectedMonth" @change="fetchMonthlyCosts">
                  <option value="">All Months</option>
                  <option v-for="month in months" :key="month.value" :value="month.value">
                    {{ month.label }}
                  </option>
                </select>
                <select v-model="selectedYear" @change="fetchMonthlyCosts">
                  <option value="">All Years</option>
                  <option v-for="year in years" :key="year" :value="year">
                    {{ year }}
                  </option>
                </select>
              </div>
            </div>
            <button class="export-btn" @click="exportToCSV">
              <i class="fa-solid fa-file-export"></i>
              Export to CSV
            </button>
          </div>

          <!-- Records Table -->
          <div v-if="!selectedSubsidiary" class="no-records">
            <i class="fa-solid fa-circle-info info-icon"></i>
            <p>Please select a subsidiary to view cost records.</p>
          </div>
          <div v-else-if="monthlyCosts.length === 0" class="no-records">
            <i class="fa-solid fa-circle-info info-icon"></i>
            <p>No records found for the selected criteria.</p>
          </div>
          <div v-else class="costs-table">
            <div v-for="(group, key) in groupedMonthlyCosts" :key="key" class="month-group">
              <div class="month-header">
                <h3>{{ formatMonthName(group.month) }}</h3>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Category</th>
                    <th>Amount (RM)</th>
                    <th>Submitted By</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="cost in group.records" :key="cost.id" @click="openMobileEdit(cost)">
                    <td>{{ cost.expense_type }}</td>
                    <td>
                      <el-tag 
                        :type="cost.category === 'stock' ? 'primary' : cost.category === 'cost' ? 'success' : 'warning'" 
                        size="small"
                      >
                        {{ cost.category }}
                      </el-tag>
                    </td>
                    <td>
                      <span v-if="!cost.isEditing"><CurrencyDisplay :value="cost.amount" /></span>
                      <input 
                        v-else 
                        type="number" 
                        v-model="cost.editedAmount" 
                        step="0.01" 
                        min="0"
                        class="edit-input"
                      />
                    </td>
                    <td>{{ cost.created_by_name }}</td>
                    <td>
                      <div class="action-buttons">
                        <button 
                          v-if="!cost.isEditing"
                          @click.stop="startEditing(cost)" 
                          class="edit-btn"
                        >
                          <i class="fa-solid fa-edit"></i>
                        </button>
                        <button 
                          v-if="cost.isEditing"
                          @click="saveEdit(cost)" 
                          class="save-btn"
                        >
                          <i class="fa-solid fa-check"></i>
                        </button>
                        <button 
                          v-if="cost.isEditing"
                          @click="cancelEdit(cost)" 
                          class="cancel-btn"
                        >
                          <i class="fa-solid fa-xmark"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
              <!-- Add total inputs for manage section -->
              <div class="total-inputs-container">
                <div class="total-input-container">
                  <label>Total Cost (RM)</label>
                  <div class="total-input-wrapper">
                    <input 
                      type="number" 
                      v-model="group.totalCost" 
                      step="0.01" 
                      min="0" 
                      placeholder="0.00"
                      class="total-input"
                    />
                    <div class="total-actions">
                      <button 
                        @click="saveTotals(group)" 
                        class="save-btn"
                      >
                        <i class="fa-solid fa-check"></i>
                      </button>
                      <button 
                        @click="cancelEditTotals(group)" 
                        class="cancel-btn"
                      >
                        <i class="fa-solid fa-xmark"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <div class="total-input-container">
                  <label>Total Expenses (RM)</label>
                  <div class="total-input-wrapper">
                    <input 
                      type="number" 
                      v-model="group.totalExpenses" 
                      step="0.01" 
                      min="0" 
                      placeholder="0.00"
                      class="total-input"
                    />
                    <div class="total-actions">
                      <button 
                        @click="saveTotals(group)" 
                        class="save-btn"
                      >
                        <i class="fa-solid fa-check"></i>
                      </button>
                      <button 
                        @click="cancelEditTotals(group)" 
                        class="cancel-btn"
                      >
                        <i class="fa-solid fa-xmark"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Add Record Modal -->
          <div v-if="showModal" class="modal-overlay">
            <div class="modal-content">
              <div class="modal-header">
                <h3>Add New Record</h3>
                <button @click="closeModal" class="close-btn">
                  <i class="fa-solid fa-xmark"></i>
                </button>
              </div>
              <div class="modal-body">
                <div class="form-group">
                  <label>Entry Date (Month/Year)</label>
                  <div class="month-year-select">
                    <select v-model="newRecord.month" required>
                      <option value="">Select Month</option>
                      <option v-for="month in months" :key="month.value" :value="month.value">
                        {{ month.label }}
                      </option>
                    </select>
                    <select v-model="newRecord.year" required>
                      <option value="">Select Year</option>
                      <option v-for="year in years" :key="year" :value="year">
                        {{ year }}
                      </option>
                    </select>
                  </div>
                </div>

                <div class="form-group">
                  <label>Branch</label>
                  <div class="month-year-select">
                    <select v-model="newRecord.branchId" required>
                      <option value="">Select Branch</option>
                      <option v-for="branch in branches" :key="branch.id" :value="branch.id">
                        {{ branch.branch_name }}
                      </option>
                    </select>
                  </div>
                </div>

                <!-- Costs Section -->
                <div class="category-section">
                  <h3>Costs</h3>
                  <div class="total-input-container">
                    <label>Total Cost (RM)</label>
                    <input 
                      type="number" 
                      v-model="newRecord.totalCost" 
                      step="0.01" 
                      min="0" 
                      placeholder="0.00"
                      class="total-input"
                    />
                  </div>
                  <div class="table-container">
                    <table class="input-table">
                      <thead>
                        <tr>
                          <th>Cost Type</th>
                          <th>Amount (RM)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="type in costTypes" :key="type.id">
                          <td>{{ type.type }}</td>
                          <td>
                            <input 
                              type="number" 
                              v-model="newRecord.costs[type.id]" 
                              step="0.01" 
                              min="0" 
                              placeholder="0.00"
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- Expenses Section -->
                <div class="category-section">
                  <h3>Expenses</h3>
                  <div class="total-input-container">
                    <label>Total Expenses (RM)</label>
                    <input 
                      type="number" 
                      v-model="newRecord.totalExpenses" 
                      step="0.01" 
                      min="0" 
                      placeholder="0.00"
                      class="total-input"
                    />
                  </div>
                  <div class="table-container">
                    <table class="input-table">
                      <thead>
                        <tr>
                          <th>Expense Type</th>
                          <th>Amount (RM)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="type in expenseTypes" :key="type.id">
                          <td>{{ type.type }}</td>
                          <td>
                            <input 
                              type="number" 
                              v-model="newRecord.expenses[type.id]" 
                              step="0.01" 
                              min="0" 
                              placeholder="0.00"
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div class="modal-footer">
                <button @click="closeModal" class="cancel-btn">Cancel</button>
                <button @click="submitNewRecord" class="submit-btn" :disabled="isSubmitting">
                  <span v-if="isSubmitting">Submitting...</span>
                  <span v-else>Submit All</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Mobile Edit Modal -->
          <div v-if="isMobile && mobileEditRow" class="modal-overlay">
            <div class="modal-content" style="max-width: 350px;">
              <div class="modal-header">
                <h3>Edit Amount</h3>
                <button @click="closeMobileEdit" class="close-btn">
                  <i class="fa-solid fa-xmark"></i>
                </button>
              </div>
              <div class="modal-body">
                <div class="form-group">
                  <label>Type</label>
                  <div>{{ mobileEditRow.expense_type }}</div>
                </div>
                <div class="form-group">
                  <label>Category</label>
                  <el-tag :type="mobileEditRow.category === 'cost' ? 'success' : 'warning'" size="small">
                    {{ mobileEditRow.category }}
                  </el-tag>
                </div>
                <div class="form-group">
                  <label>Amount (RM)</label>
                  <input type="number" v-model="mobileEditValue" step="0.01" min="0" />
                </div>
              </div>
              <div class="modal-footer">
                <button @click="closeMobileEdit" class="cancel-btn">Cancel</button>
                <button @click="saveMobileEdit" class="submit-btn">Save</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Bulk Entry Tab -->
      <div v-if="activeTab === 'bulk'" class="tab-content">
        <div class="filter-description">
          <p>
            <i class="fa-solid fa-circle-info"></i>
            Select a subsidiary and year to enter total costs and expenses for all 12 months at once.
          </p>
        </div>

        <div class="submit-container">
          <div class="card-header">
            <div class="header-icon">
              <i class="fa-solid fa-table"></i>
            </div>
            <h2>Bulk Entry - Total Costs & Expenses</h2>
          </div>

          <div class="submission-form">
            <div class="form-section">
              <div class="form-group">
                <label>Subsidiary</label>
                <div class="subsidiary-selector">
                  <div class="selected-subsidiary" @click="toggleBulkSubsidiaryDropdown">
                    <span>{{ selectedBulkSubsidiaryName }}</span>
                    <i class="fa-solid fa-chevron-down" :class="{ 'rotate': showBulkSubsidiaryDropdown }"></i>
                  </div>
                  <div class="subsidiary-dropdown" v-if="showBulkSubsidiaryDropdown">
                    <div class="search-box">
                      <input 
                        type="text" 
                        v-model="bulkSubsidiarySearch" 
                        placeholder="Search subsidiaries..."
                        @click.stop
                      >
                    </div>
                    <div 
                      v-for="sub in filteredBulkSubsidiaries" 
                      :key="sub.id" 
                      class="subsidiary-option"
                      :class="{ 'active': selectedBulkSubsidiary === sub.id }"
                      @click="selectBulkSubsidiary(sub)"
                    >
                      {{ sub.name }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="form-group">
                <label>Year</label>
                <select v-model="bulkYear" @change="fetchBulkTotals" required>
                  <option value="">Select Year</option>
                  <option v-for="year in years" :key="year" :value="year">
                    {{ year }}
                  </option>
                </select>
              </div>
            </div>

            <div v-if="!selectedBulkSubsidiary" class="category-section info-message">
              <i class="fa-solid fa-circle-info info-icon"></i>
              <p>Please select a subsidiary first.</p>
            </div>
            <div v-else-if="!bulkYear" class="category-section info-message">
              <i class="fa-solid fa-circle-info info-icon"></i>
              <p>Please select a year.</p>
            </div>
            <div v-else class="bulk-entry-table">
              <div class="table-header">
                <h3>Monthly Totals for {{ bulkYear }}</h3>
                <button @click="copyToAllMonths" class="copy-all-btn">
                  <i class="fa-solid fa-copy"></i>
                  Copy First Row to All
                </button>
              </div>
              <table class="bulk-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Total Cost (RM)</th>
                    <th>Total Expenses (RM)</th>
                    <th>Depreciation of Fixed Assets (RM)</th>
                    <th>Finance Cost (RM)</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(month, index) in bulkMonths" :key="index">
                    <td class="month-label">
                      <strong>{{ month.label }}</strong>
                    </td>
                    <td>
                      <input 
                        type="number" 
                        v-model="bulkData[index].totalCost" 
                        step="0.01" 
                        min="0" 
                        placeholder="0.00"
                        class="bulk-input"
                      />
                    </td>
                    <td>
                      <input 
                        type="number" 
                        v-model="bulkData[index].totalExpenses" 
                        step="0.01" 
                        min="0" 
                        placeholder="0.00"
                        class="bulk-input"
                      />
                    </td>
                    <td>
                      <input 
                        type="number" 
                        v-model="bulkData[index].depreciationOfFixedAssets" 
                        step="0.01" 
                        min="0" 
                        placeholder="0.00"
                        class="bulk-input"
                      />
                    </td>
                    <td>
                      <input 
                        type="number" 
                        v-model="bulkData[index].financeCost" 
                        step="0.01" 
                        min="0" 
                        placeholder="0.00"
                        class="bulk-input"
                      />
                    </td>
                    <td>
                      <span v-if="bulkData[index].exists" class="status-badge exists">
                        <i class="fa-solid fa-check-circle"></i>
                        Exists
                      </span>
                      <span v-else class="status-badge new">
                        <i class="fa-solid fa-plus-circle"></i>
                        New
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="selectedBulkSubsidiary && bulkYear" class="form-actions">
              <button 
                @click="submitBulkTotals" 
                class="submit-btn"
                :disabled="isSubmitting"
              >
                <i class="fa-solid fa-upload"></i>
                <span v-if="isSubmitting">Submitting...</span>
                <span v-else>Submit All Months</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Submit Costs Tab -->
      <div v-if="activeTab === 'submit'" class="tab-content">
        <div class="filter-description">
          <p>
            <i class="fa-solid fa-circle-info"></i>
            Select a subsidiary and enter the cost details for submission.
          </p>
        </div>

        <div class="submit-container">
          <div class="card-header">
            <div class="header-icon">
              <i class="fa-solid fa-file-invoice-dollar"></i>
            </div>
            <h2>Submit New Costs</h2>
          </div>

          <!-- Submission Form -->
          <div class="submission-form">
            <div class="form-section">
              <div class="form-group">
                <label>Subsidiary</label>
                <div class="subsidiary-selector">
                  <div class="selected-subsidiary" @click="toggleSubmitSubsidiaryDropdown">
                    <span>{{ selectedSubmitSubsidiaryName }}</span>
                    <i class="fa-solid fa-chevron-down" :class="{ 'rotate': showSubmitSubsidiaryDropdown }"></i>
                  </div>
                  <div class="subsidiary-dropdown" v-if="showSubmitSubsidiaryDropdown">
                    <div class="search-box">
                      <input 
                        type="text" 
                        v-model="submitSubsidiarySearch" 
                        placeholder="Search subsidiaries..."
                        @click.stop
                      >
                    </div>
                    <div 
                      v-for="sub in filteredSubmitSubsidiaries" 
                      :key="sub.id" 
                      class="subsidiary-option"
                      :class="{ 'active': selectedSubmitSubsidiary === sub.id }"
                      @click="selectSubmitSubsidiary(sub)"
                    >
                      {{ sub.name }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="form-group">
                <label>Entry Date</label>
                <div class="month-year-select">
                  <select v-model="submitForm.month" required @change="checkExistingRecords">
                    <option value="">Select Month</option>
                    <option v-for="month in months" :key="month.value" :value="month.value">
                      {{ month.label }}
                    </option>
                  </select>
                  <select v-model="submitForm.year" required @change="checkExistingRecords">
                    <option value="">Select Year</option>
                    <option v-for="year in years" :key="year" :value="year">
                      {{ year }}
                    </option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Costs Section -->
            <div v-if="!selectedSubmitSubsidiary" class="category-section info-message">
              <i class="fa-solid fa-circle-info info-icon"></i>
              <p>Please select a subsidiary first.</p>
            </div>
            <div v-else-if="!submitForm.month || !submitForm.year" class="category-section info-message">
              <i class="fa-solid fa-circle-info info-icon"></i>
              <p>Please select month and year.</p>
            </div>
            <div v-else-if="existingRecordsForMonth" class="category-section info-message">
              <i class="fa-solid fa-circle-info info-icon"></i>
              <p>Records already exist for the selected month and year. Please select a different month/year.</p>
            </div>
            <template v-else>
              <!-- Stock Section -->
              <div class="category-section">
                <h3>Stock</h3>
                <div class="table-container">
                  <table class="input-table">
                    <thead>
                      <tr>
                        <th>Stock Type</th>
                        <th>Amount (RM)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr 
                        v-for="type in stockTypes" 
                        :key="type.id"
                        draggable="true"
                        @dragstart="handleDragStart($event, type, 'stock')"
                        @dragover.prevent
                        @drop="handleDrop($event, type, 'stock')"
                        :class="{ 'dragging': isDragging && draggedItem?.id === type.id }"
                      >
                        <td>
                          <div class="drag-handle">
                            <i class="fa-solid fa-grip-vertical"></i>
                            {{ type.type }}
                          </div>
                        </td>
                        <td>
                          <input 
                            type="number" 
                            v-model="submitForm.stocks[type.id]" 
                            step="0.01" 
                            min="0" 
                            placeholder="0.00"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div class="category-section">
                <h3>Costs</h3>
                <div class="table-container">
                  <table class="input-table">
                    <thead>
                      <tr>
                        <th>Cost Type</th>
                        <th>Amount (RM)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr 
                        v-for="type in costTypes" 
                        :key="type.id"
                        draggable="true"
                        @dragstart="handleDragStart($event, type, 'cost')"
                        @dragover.prevent
                        @drop="handleDrop($event, type, 'cost')"
                        :class="{ 'dragging': isDragging && draggedItem?.id === type.id }"
                      >
                        <td>
                          <div class="drag-handle">
                            <i class="fa-solid fa-grip-vertical"></i>
                            {{ type.type }}
                          </div>
                        </td>
                        <td>
                          <input 
                            type="number" 
                            v-model="submitForm.costs[type.id]" 
                            step="0.01" 
                            min="0" 
                            placeholder="0.00"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div class="total-input-container">
                  <label>Total Cost (RM)</label>
                  <input 
                    type="number" 
                    v-model="submitForm.totalCost" 
                    step="0.01" 
                    min="0" 
                    placeholder="0.00"
                    class="total-input"
                  />
                </div>
              </div>

              <!-- Expenses Section -->
              <div class="category-section">
                <h3>Expenses</h3>
                <div class="table-container">
                  <table class="input-table">
                    <thead>
                      <tr>
                        <th>Expense Type</th>
                        <th>Amount (RM)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr 
                        v-for="type in expenseTypes" 
                        :key="type.id"
                        draggable="true"
                        @dragstart="handleDragStart($event, type, 'expense')"
                        @dragover.prevent
                        @drop="handleDrop($event, type, 'expense')"
                        :class="{ 'dragging': isDragging && draggedItem?.id === type.id }"
                      >
                        <td>
                          <div class="drag-handle">
                            <i class="fa-solid fa-grip-vertical"></i>
                            {{ type.type }}
                          </div>
                        </td>
                        <td>
                          <input 
                            type="number" 
                            v-model="submitForm.expenses[type.id]" 
                            step="0.01" 
                            min="0" 
                            placeholder="0.00"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div class="total-input-container">
                  <label>Total Expenses (RM)</label>
                  <div class="total-input-wrapper">
                    <input 
                      type="number" 
                      v-model="submitForm.totalExpenses" 
                      step="0.01" 
                      min="0" 
                      placeholder="0.00"
                      class="total-input"
                    />
                    <div class="total-actions">
                      <button 
                        @click="saveTotals(group)" 
                        class="save-btn"
                      >
                        <i class="fa-solid fa-check"></i>
                      </button>
                      <button 
                        @click="cancelEditTotals(group)" 
                        class="cancel-btn"
                      >
                        <i class="fa-solid fa-xmark"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <div class="form-actions">
              <button 
                @click="submitNewCosts" 
                class="submit-btn"
              >
                <span v-if="isSubmitting">Submitting...</span>
                <span v-else>Submit All</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, onUnmounted, reactive } from 'vue'
import { ElMessage } from 'element-plus'

const selectedMonth = ref('')
const selectedYear = ref(new Date().getFullYear().toString())
const selectedSubsidiary = ref('')
const subsidiaries = ref([])
const monthlyCosts = ref([])
const showSubsidiaryDropdown = ref(false)
const subsidiarySearch = ref('')
const showModal = ref(false)
const availableExpenseTypes = ref([])
const newRecord = ref({
  month: '',
  year: '',
  subsidiaryId: '',
  costs: {},
  expenses: {},
  totalCost: 0,
  totalExpenses: 0
})

// Add new state variables for submit tab
const activeTab = ref('submit')
const selectedSubmitSubsidiary = ref('')
const showSubmitSubsidiaryDropdown = ref(false)
const submitSubsidiarySearch = ref('')
const submitForm = ref({
  month: '',
  year: '2025',
  costs: {},
  expenses: {},
  stocks: {},
  totalCost: 0,
  totalExpenses: 0
})

// Add new state variables for bulk entry tab
const selectedBulkSubsidiary = ref('')
const showBulkSubsidiaryDropdown = ref(false)
const bulkSubsidiarySearch = ref('')
const bulkYear = ref('')
const bulkData = ref([])
const bulkTotalsCache = ref({})

// Add new state variables for search
const branchSearch = ref('')

const months = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' }
]

const years = computed(() => {
  const currentYear = new Date().getFullYear()
  return Array.from({ length: 6 }, (_, i) => (currentYear - 2 + i).toString())
})

const selectedSubsidiaryName = computed(() => {
  const selected = subsidiaries.value.find(sub => sub.id === selectedSubsidiary.value)
  return selected ? selected.name : 'Select Subsidiary'
})

const filteredSubsidiaries = computed(() => {
  if (!subsidiarySearch.value) return subsidiaries.value
  const query = subsidiarySearch.value.toLowerCase()
  return subsidiaries.value.filter(sub => 
    sub.name.toLowerCase().includes(query)
  )
})

const fetchSubsidiaries = async () => {
  try {
    const token = sessionStorage.getItem('token')
    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/subsidiary`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || 'Failed to fetch subsidiaries')
    }
    
    subsidiaries.value = (await response.json()).sort((a, b) => a.id - b.id)
  } catch (error) {
    ElMessage.error(error.message || 'Failed to load subsidiaries. Please try again.')
    subsidiaries.value = []
  }
}

const fetchMonthlyCosts = async () => {
  try {
    if (!selectedSubsidiary.value) {
      monthlyCosts.value = []
      return
    }

    const token = sessionStorage.getItem('token')
    const params = new URLSearchParams({
      year: selectedYear.value,
      month: selectedMonth.value
    })

    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/cost-submission/monthly/${selectedSubsidiary.value}?${params.toString()}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || 'Failed to fetch monthly costs')
    }
    
    const data = await response.json()

    // Group the data by month and include totals
    const groupedData = {}
    data.forEach(cost => {
      const month = cost.entry_month_year
      if (!groupedData[month]) {
        groupedData[month] = {
          total_cost: 0,
          total_expenses: 0,
          records: []
        }
      }
      groupedData[month].records.push(cost)
    })

    // Fetch totals for each month
    for (const month in groupedData) {
      const entryDate = `${month.substring(0, 4)}${month.substring(4, 6)}01`
      const totalsResponse = await fetch(`${import.meta.env.VITE_API_URL}/api/cost-submission/totals/${selectedSubsidiary.value}/${entryDate}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (totalsResponse.ok) {
        const totalsData = await totalsResponse.json()
        if (totalsData) {
          groupedData[month].total_cost = totalsData.total_cost
          groupedData[month].total_expenses = totalsData.total_expenses
        }
      }
    }

    // Flatten the grouped data back into an array
    monthlyCosts.value = Object.entries(groupedData).flatMap(([month, data]) => 
      data.records.map(record => ({
        ...record,
        total_cost: data.total_cost,
        total_expenses: data.total_expenses
      }))
    )

  } catch (error) {
    ElMessage.error(error.message || 'Failed to load monthly records. Please try again.')
    monthlyCosts.value = []
  }
}

const handleClickOutside = (event) => {
  const subsidiarySelector = document.querySelector('.subsidiary-selector')
  const selectedSubsidiary = document.querySelector('.selected-subsidiary')

  if (subsidiarySelector && !subsidiarySelector.contains(event.target) && !selectedSubsidiary?.contains(event.target)) {
    showSubsidiaryDropdown.value = false
  }
}

const toggleSubsidiaryDropdown = (event) => {
  event.stopPropagation()
  showSubsidiaryDropdown.value = !showSubsidiaryDropdown.value
}

const selectSubsidiary = async (subsidiary) => {
  selectedSubsidiary.value = subsidiary.id
  showSubsidiaryDropdown.value = false
  await fetchMonthlyCosts()
}

const startEditing = (cost) => {
  cost.isEditing = true
  cost.editedAmount = cost.amount
}

const cancelEdit = (cost) => {
  cost.isEditing = false
  delete cost.editedAmount
}

const saveEdit = async (cost) => {
  try {
    const token = sessionStorage.getItem('token')
    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/cost-submission/update/${cost.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        amount: parseFloat(cost.editedAmount)
      })
    })

    if (!response.ok) {
      throw new Error('Failed to update record')
    }

    // Update the local record
    cost.amount = parseFloat(cost.editedAmount)
    cost.isEditing = false
    delete cost.editedAmount

    ElMessage.success('Record updated successfully')
  } catch (error) {
    ElMessage.error('Failed to update record. Please try again.')
  }
}

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-MY', {
    style: 'currency',
    currency: 'MYR'
  }).format(amount)
}

const groupedMonthlyCosts = computed(() => {
  const groups = {}
  
  monthlyCosts.value.forEach(cost => {
    const month = cost.entry_month_year
    const key = month

    if (!groups[key]) {
      groups[key] = {
        month,
        records: [],
        total: 0,
        totalCost: cost.total_cost || 0,
        totalExpenses: cost.total_expenses || 0,
        isEditingTotals: false
      }
    }
    groups[key].records.push(cost)
    groups[key].total += parseFloat(cost.amount)
  })

  // Sort by month (descending) and sort records within each group
  return Object.fromEntries(
    Object.entries(groups).sort(([a], [b]) => {
      // Sort by month (descending)
      return b.localeCompare(a)
    }).map(([key, group]) => {
      // Sort records: stocks first (reverse alphabetical), then costs and expenses (alphabetical)
      group.records.sort((a, b) => {
        // First sort by category (stock before cost before expense)
        if (a.category !== b.category) {
          if (a.category === 'stock') return -1
          if (b.category === 'stock') return 1
          if (a.category === 'cost') return -1
          if (b.category === 'cost') return 1
          return 0
        }
        // Then sort by type name
        if (a.category === 'stock') {
          return b.expense_type.localeCompare(a.expense_type) // Reverse alphabetical for stocks
        }
        return a.expense_type.localeCompare(b.expense_type) // Normal alphabetical for costs and expenses
      })
      return [key, group]
    })
  )
})

const formatMonthName = (monthString) => {
  const year = monthString.substring(0, 4);
  const month = monthString.substring(4, 6);
  const date = new Date(year, parseInt(month) - 1);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
};

const costTypes = computed(() => {
  return availableExpenseTypes.value
    .filter(type => type.category === 'cost')
    .sort((a, b) => a.type.localeCompare(b.type))
})

const expenseTypes = computed(() => {
  return availableExpenseTypes.value
    .filter(type => type.category === 'expense')
    .sort((a, b) => a.type.localeCompare(b.type))
})

const stockTypes = computed(() => {
  return availableExpenseTypes.value
    .filter(type => type.category === 'stock')
    .sort((a, b) => b.type.localeCompare(a.type)) // Reverse alphabetical order
})

const showAddRecordModal = async () => {
  try {
    const token = sessionStorage.getItem('token')
    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/cost-submission/expense-types/${selectedSubsidiary.value}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    if (!response.ok) throw new Error('Failed to fetch expense types')
    availableExpenseTypes.value = await response.json()
    
    // Initialize the form with current month/year
    const today = new Date()
    newRecord.value = {
      month: (today.getMonth() + 1).toString().padStart(2, '0'),
      year: today.getFullYear().toString(),
      subsidiaryId: selectedSubsidiary.value,
      costs: {},
      expenses: {},
      totalCost: 0,
      totalExpenses: 0
    }
    showModal.value = true
  } catch (error) {
    ElMessage.error('Failed to load expense types. Please try again.')
  }
}

const closeModal = () => {
  showModal.value = false
  newRecord.value = {
    month: '',
    year: '',
    subsidiaryId: '',
    costs: {},
    expenses: {},
    totalCost: 0,
    totalExpenses: 0
  }
}

const isSubmitting = ref(false)

const submitNewRecord = async () => {
  try {
    if (!newRecord.value.month || !newRecord.value.year || !newRecord.value.subsidiaryId) {
      ElMessage.warning('Please fill in all required fields')
      return
    }

    isSubmitting.value = true
    const token = sessionStorage.getItem('token')
    const entryDate = `${newRecord.value.year}${newRecord.value.month}`
    
    // Process all costs and expenses
    const processedCosts = {}
    const processedExpenses = {}
    
    // Initialize all cost types with 0
    costTypes.value.forEach(type => {
      processedCosts[type.id] = newRecord.value.costs[type.id] === undefined || 
                               newRecord.value.costs[type.id] === null || 
                               newRecord.value.costs[type.id] === '' ? 
                               0 : parseFloat(newRecord.value.costs[type.id])
    })
    
    // Initialize all expense types with 0
    expenseTypes.value.forEach(type => {
      processedExpenses[type.id] = newRecord.value.expenses[type.id] === undefined || 
                                  newRecord.value.expenses[type.id] === null || 
                                  newRecord.value.expenses[type.id] === '' ? 
                                  0 : parseFloat(newRecord.value.expenses[type.id])
    })

    // Submit all costs
    for (const [typeId, amount] of Object.entries(processedCosts)) {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/cost-submission/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          expenseTypeId: parseInt(typeId),
          amount: amount,
          subsidiaryId: selectedSubsidiary.value,
          entryDate: entryDate
        })
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to submit cost record')
      }
    }

    // Submit all expenses
    for (const [typeId, amount] of Object.entries(processedExpenses)) {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/cost-submission/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          expenseTypeId: parseInt(typeId),
          amount: amount,
          subsidiaryId: selectedSubsidiary.value,
          entryDate: entryDate
        })
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to submit expense record')
      }
    }

    ElMessage.success('Records submitted successfully')
    closeModal()
    fetchMonthlyCosts()
  } catch (error) {
    ElMessage.error(error.message || 'Failed to submit records. Please try again.')
  } finally {
    isSubmitting.value = false
  }
}

const exportToCSV = () => {
  // Prepare the data for export
  const data = [
    ['Type', 'Category', 'Amount (RM)', 'Submitted By', 'Month/Year']
  ]

  // Flatten all records from groupedMonthlyCosts
  Object.entries(groupedMonthlyCosts.value).forEach(([month, group]) => {
    group.records.forEach(cost => {
      data.push([
        `"${cost.expense_type}"`,
        `"${cost.category}"`,
        `"${cost.amount}"`,
        `"${cost.created_by_name}"`,
        `"${month}"`
      ])
    })
  })

  // Convert to CSV string
  const csvContent = data.map(row => row.join(',')).join('\n')

  // Create and trigger download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', `cost_records.csv`)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const isMobile = ref(window.innerWidth <= 768)
const mobileEditRow = ref(null)
const mobileEditValue = ref('')

const handleResize = () => {
  isMobile.value = window.innerWidth <= 768
}

const openMobileEdit = (cost) => {
  if (!isMobile.value) return
  mobileEditRow.value = cost
  mobileEditValue.value = cost.amount
}

const closeMobileEdit = () => {
  mobileEditRow.value = null
  mobileEditValue.value = ''
}

const saveMobileEdit = async () => {
  if (!mobileEditRow.value) return
  try {
    const token = sessionStorage.getItem('token')
    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/cost-submission/update/${mobileEditRow.value.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        amount: parseFloat(mobileEditValue.value)
      })
    })
    if (!response.ok) throw new Error('Failed to update record')
    mobileEditRow.value.amount = parseFloat(mobileEditValue.value)
    ElMessage.success('Record updated successfully')
    closeMobileEdit()
  } catch (error) {
    ElMessage.error('Failed to update record. Please try again.')
  }
}

const getBranchName = (branchId) => {
  const branch = branches.value.find(b => b.id === branchId)
  return branch ? branch.branch_name : ''
}

// Add computed properties for submit tab
const selectedSubmitSubsidiaryName = computed(() => {
  const selected = subsidiaries.value.find(sub => sub.id === selectedSubmitSubsidiary.value)
  return selected ? selected.name : 'Select Subsidiary'
})

const filteredSubmitSubsidiaries = computed(() => {
  if (!submitSubsidiarySearch.value) return subsidiaries.value
  const query = submitSubsidiarySearch.value.toLowerCase()
  return subsidiaries.value.filter(sub => 
    sub.name.toLowerCase().includes(query)
  )
})

const selectedBulkSubsidiaryName = computed(() => {
  const selected = subsidiaries.value.find(sub => sub.id === selectedBulkSubsidiary.value)
  return selected ? selected.name : 'Select Subsidiary'
})

const filteredBulkSubsidiaries = computed(() => {
  if (!bulkSubsidiarySearch.value) return subsidiaries.value
  const query = bulkSubsidiarySearch.value.toLowerCase()
  return subsidiaries.value.filter(sub => 
    sub.name.toLowerCase().includes(query)
  )
})

const bulkMonths = computed(() => {
  return months.map(month => ({
    value: month.value,
    label: month.label,
    key: `${bulkYear.value}${month.value}`
  }))
})

const isFormValid = computed(() => {
  return selectedSubmitSubsidiary.value && 
         submitForm.value.month && 
         submitForm.value.year
})

// Add methods for submit tab
const toggleSubmitSubsidiaryDropdown = (event) => {
  event.stopPropagation()
  showSubmitSubsidiaryDropdown.value = !showSubmitSubsidiaryDropdown.value
}

const selectSubmitSubsidiary = async (subsidiary) => {
  selectedSubmitSubsidiary.value = subsidiary.id
  showSubmitSubsidiaryDropdown.value = false
  await fetchExpenseTypes(subsidiary.id)
  // Reset month and year when subsidiary changes
  submitForm.value.month = ''
  submitForm.value.year = '2025' // Keep the default year
}

const fetchExpenseTypes = async (subsidiaryId) => {
  try {
    const token = sessionStorage.getItem('token')
    
    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/cost-submission/expense-types/${subsidiaryId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    if (!response.ok) throw new Error('Failed to fetch expense types')
    const data = await response.json()
    availableExpenseTypes.value = data
    
    const today = new Date()
    submitForm.value = {
      month: (today.getMonth() + 1).toString().padStart(2, '0'),
      year: today.getFullYear().toString(),
      costs: {},
      expenses: {},
      stocks: {},
      totalCost: 0,
      totalExpenses: 0
    }

    // Initialize costs, expenses, and stocks with 0 for each type
    if (data && data.length > 0) {
      data.forEach(type => {
        if (type.category === 'cost') {
          submitForm.value.costs[type.id] = 0
        } else if (type.category === 'expense') {
          submitForm.value.expenses[type.id] = 0
        } else if (type.category === 'stock') {
          submitForm.value.stocks[type.id] = 0
        }
      })
    }
  } catch (error) {
    ElMessage.error('Failed to load expense types. Please try again.')
  }
}

const submitNewCosts = async () => {
  try {
    if (!selectedSubmitSubsidiary.value) {
      ElMessage.warning('Please select a subsidiary')
      return
    }
    if (!submitForm.value.month || !submitForm.value.year) {
      ElMessage.warning('Please select both month and year')
      return
    }

    isSubmitting.value = true
    const token = sessionStorage.getItem('token')
    const entryMonthYear = `${submitForm.value.year}${submitForm.value.month}` // YYYYMM for expense_entries
    const entryDate = `${submitForm.value.year}${submitForm.value.month}01` // YYYYMMDD for cost_expense_totals
    
    // Process all costs, expenses, and stocks
    const processedCosts = {}
    const processedExpenses = {}
    const processedStocks = {}
    
    // Initialize all cost types with 0
    costTypes.value.forEach(type => {
      processedCosts[type.id] = submitForm.value.costs[type.id] === undefined || 
                               submitForm.value.costs[type.id] === null || 
                               submitForm.value.costs[type.id] === '' ? 
                               0 : parseFloat(submitForm.value.costs[type.id])
    })
    
    // Initialize all expense types with 0
    expenseTypes.value.forEach(type => {
      processedExpenses[type.id] = submitForm.value.expenses[type.id] === undefined || 
                                  submitForm.value.expenses[type.id] === null || 
                                  submitForm.value.expenses[type.id] === '' ? 
                                  0 : parseFloat(submitForm.value.expenses[type.id])
    })

    // Initialize all stock types with 0
    stockTypes.value.forEach(type => {
      processedStocks[type.id] = submitForm.value.stocks[type.id] === undefined || 
                                submitForm.value.stocks[type.id] === null || 
                                submitForm.value.stocks[type.id] === '' ? 
                                0 : parseFloat(submitForm.value.stocks[type.id])
    })

    // Submit all costs
    for (const [typeId, amount] of Object.entries(processedCosts)) {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/cost-submission/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          expenseTypeId: parseInt(typeId),
          amount: amount,
          subsidiaryId: selectedSubmitSubsidiary.value,
          entryDate: entryMonthYear // Use YYYYMM format for expense entries
        })
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to submit cost record')
      }
    }

    // Submit all expenses
    for (const [typeId, amount] of Object.entries(processedExpenses)) {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/cost-submission/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          expenseTypeId: parseInt(typeId),
          amount: amount,
          subsidiaryId: selectedSubmitSubsidiary.value,
          entryDate: entryMonthYear // Use YYYYMM format for expense entries
        })
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to submit expense record')
      }
    }

    // Submit all stocks
    for (const [typeId, amount] of Object.entries(processedStocks)) {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/cost-submission/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          expenseTypeId: parseInt(typeId),
          amount: amount,
          subsidiaryId: selectedSubmitSubsidiary.value,
          entryDate: entryMonthYear // Use YYYYMM format for expense entries
        })
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to submit stock record')
      }
    }

    // Submit total cost and expenses
    const totalResponse = await fetch(`${import.meta.env.VITE_API_URL}/api/cost-submission/totals`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        subsidiaryId: selectedSubmitSubsidiary.value,
        entryDate: entryDate, // Use YYYYMMDD format for cost_expense_totals
        totalCost: submitForm.value.totalCost,
        totalExpenses: submitForm.value.totalExpenses
      })
    })

    if (!totalResponse.ok) {
      const errorData = await totalResponse.json()
      throw new Error(errorData.message || 'Failed to submit total records')
    }

    ElMessage.success('Records submitted successfully')
    
    // Reset form
    submitForm.value = {
      month: '',
      year: '2025',
      costs: {},
      expenses: {},
      stocks: {},
      totalCost: 0,
      totalExpenses: 0
    }
    selectedSubmitSubsidiary.value = ''
    
    // Refresh the data in manage tab
    if (selectedSubsidiary.value) {
      await fetchMonthlyCosts()
    }
  } catch (error) {
    ElMessage.error(error.message || 'Failed to submit records. Please try again.')
  } finally {
    isSubmitting.value = false
  }
}

const existingRecordsForMonth = computed(() => {
  if (!submitForm.value.month || !submitForm.value.year || !selectedSubmitSubsidiary.value) {
    return false
  }

  const entryDate = `${submitForm.value.year}${submitForm.value.month}`
  
  const hasRecords = monthlyCosts.value.some(cost => 
    cost.entry_month_year === entryDate && 
    cost.subsidiary_id === selectedSubmitSubsidiary.value
  )
  
  return hasRecords
})

const checkExistingRecords = async () => {
  if (submitForm.value.month && submitForm.value.year && selectedSubmitSubsidiary.value) {
    try {
      const token = sessionStorage.getItem('token')
      const params = new URLSearchParams({
        year: submitForm.value.year,
        month: submitForm.value.month
      })

      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/cost-submission/monthly/${selectedSubmitSubsidiary.value}?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      
      if (!response.ok) {
        throw new Error('Failed to check existing records')
      }
      
      const data = await response.json()
      monthlyCosts.value = data
    } catch (error) {
    }
  }
}

// Add these new refs and methods in the script section
const isDragging = ref(false)
const draggedItem = ref(null)
const draggedCategory = ref('')

const handleDragStart = (event, item, category) => {
  isDragging.value = true
  draggedItem.value = item
  draggedCategory.value = category
  event.dataTransfer.effectAllowed = 'move'
  event.target.classList.add('dragging')
}

const handleDrop = (event, targetItem, category) => {
  event.preventDefault()
  if (!draggedItem.value || !targetItem) return

  // Only allow reordering within the same category
  if (category !== draggedCategory.value) return

  // Get the current arrays from the underlying data source
  const items = category === 'cost' 
    ? availableExpenseTypes.value.filter(type => type.category === 'cost')
    : availableExpenseTypes.value.filter(type => type.category === 'expense')
  
  // Find the indices
  const draggedIndex = items.findIndex(item => item.id === draggedItem.value.id)
  const targetIndex = items.findIndex(item => item.id === targetItem.id)
  
  if (draggedIndex === -1 || targetIndex === -1) return

  // Create a new array with the reordered items
  const newItems = [...items]
  const [movedItem] = newItems.splice(draggedIndex, 1)
  newItems.splice(targetIndex, 0, movedItem)

  // Update the underlying data source
  const otherItems = availableExpenseTypes.value.filter(type => type.category !== category)
  availableExpenseTypes.value = [...otherItems, ...newItems]

  // Reset drag state
  isDragging.value = false
  draggedItem.value = null
  draggedCategory.value = ''
  event.target.classList.remove('dragging')
}

onMounted(() => {
  fetchSubsidiaries()
  document.addEventListener('click', handleClickOutside)
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('resize', handleResize)
})

// Add to the script section
const startEditingTotals = (group) => {
  group.isEditingTotals = true
  group.originalTotals = {
    totalCost: parseFloat(group.totalCost) || 0,
    totalExpenses: parseFloat(group.totalExpenses) || 0
  }
}

const cancelEditTotals = (group) => {
  // Reset to the original values from the API
  fetchMonthlyCosts()
}

const saveTotals = async (group) => {
  try {
    if (!group || !group.month) {
      ElMessage.error('Invalid group data. Please try again.')
      return
    }

    const token = sessionStorage.getItem('token')
    const entryDate = `${group.month.substring(0, 4)}${group.month.substring(4, 6)}01`
    
    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/cost-submission/totals`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        subsidiaryId: selectedSubsidiary.value,
        entryDate: entryDate,
        totalCost: parseFloat(group.totalCost) || 0,
        totalExpenses: parseFloat(group.totalExpenses) || 0
      })
    })

    if (!response.ok) {
      throw new Error('Failed to update totals')
    }

    ElMessage.success('Totals updated successfully')
  } catch (error) {
    ElMessage.error('Failed to update totals. Please try again.')
  }
}

// Bulk entry methods
const toggleBulkSubsidiaryDropdown = (event) => {
  event.stopPropagation()
  showBulkSubsidiaryDropdown.value = !showBulkSubsidiaryDropdown.value
}

const selectBulkSubsidiary = async (subsidiary) => {
  selectedBulkSubsidiary.value = subsidiary.id
  showBulkSubsidiaryDropdown.value = false
  bulkSubsidiarySearch.value = ''
  if (bulkYear.value) {
    await fetchBulkTotals()
  }
}

const fetchBulkTotals = async () => {
  if (!selectedBulkSubsidiary.value || !bulkYear.value) {
    bulkData.value = []
    return
  }

  try {
    const token = sessionStorage.getItem('token')
    
    // Initialize bulk data for all 12 months
    bulkData.value = months.map(month => ({
      month: month.value,
      totalCost: '',
      totalExpenses: '',
      depreciationOfFixedAssets: '',
      financeCost: '',
      exists: false
    }))

    // Fetch existing totals for each month
    for (let i = 0; i < months.length; i++) {
      const month = months[i].value
      const entryDate = `${bulkYear.value}${month}01`
      
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/cost-submission/totals/${selectedBulkSubsidiary.value}/${entryDate}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.ok) {
          const totals = await response.json()
          if (totals) {
            bulkData.value[i].totalCost = totals.total_cost || ''
            bulkData.value[i].totalExpenses = totals.total_expenses || ''
            bulkData.value[i].depreciationOfFixedAssets = totals.depreciation_of_fixed_assets || ''
            bulkData.value[i].financeCost = totals.finance_cost || ''
            bulkData.value[i].exists = true
          }
        }
      } catch (error) {
      }
    }
  } catch (error) {
    ElMessage.error('Failed to load existing totals')
  }
}

const copyToAllMonths = () => {
  if (bulkData.value.length === 0) return
  
  const firstRow = bulkData.value[0]
  bulkData.value.forEach((row, index) => {
    if (index > 0) {
      row.totalCost = firstRow.totalCost
      row.totalExpenses = firstRow.totalExpenses
      row.depreciationOfFixedAssets = firstRow.depreciationOfFixedAssets
      row.financeCost = firstRow.financeCost
    }
  })
  ElMessage.success('Copied first row to all months')
}

const submitBulkTotals = async () => {
  if (!selectedBulkSubsidiary.value || !bulkYear.value) {
    ElMessage.warning('Please select subsidiary and year')
    return
  }

  try {
    isSubmitting.value = true
    const token = sessionStorage.getItem('token')
    let successCount = 0
    let errorCount = 0

    for (let i = 0; i < bulkData.value.length; i++) {
      const monthData = bulkData.value[i]
      const entryDate = `${bulkYear.value}${monthData.month}01`
      
      // Skip if all fields are empty
      if (!monthData.totalCost && !monthData.totalExpenses && !monthData.depreciationOfFixedAssets && !monthData.financeCost) {
        continue
      }

      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/cost-submission/totals`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            subsidiaryId: selectedBulkSubsidiary.value,
            entryDate: entryDate,
            totalCost: parseFloat(monthData.totalCost) || 0,
            totalExpenses: parseFloat(monthData.totalExpenses) || 0,
            depreciationOfFixedAssets: parseFloat(monthData.depreciationOfFixedAssets) || 0,
            financeCost: parseFloat(monthData.financeCost) || 0
          })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.message || 'Failed to submit')
        }
        
        successCount++
        bulkData.value[i].exists = true
      } catch (error) {
        errorCount++
      }
    }

    if (successCount > 0) {
      ElMessage.success(`Successfully submitted ${successCount} month${successCount > 1 ? 's' : ''}`)
      if (errorCount > 0) {
        ElMessage.warning(`${errorCount} month${errorCount > 1 ? 's' : ''} failed`)
      }
    } else if (errorCount > 0) {
      ElMessage.error('Failed to submit totals. Please try again.')
    } else {
      ElMessage.info('No data to submit')
    }
  } catch (error) {
    ElMessage.error('Failed to submit bulk totals')
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
.cost-submission {
  padding: 24px;
  background: #f8fafc;
  min-height: 100vh;
}

.container {
  width: 100%;
  margin: 0 auto;
}

.header {
  margin-bottom: 32px;
}

.header h1 {
  font-size: 1.875rem;
  font-weight: 600;
  color: #1e293b;
  letter-spacing: -0.025em;
  margin-bottom: 8px;
}

.header p {
  font-size: 0.875rem;
  color: #64748b;
}

.filter-description {
  margin-bottom: 24px;
  padding: 8px 12px;
  background: #f1f5f9;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.filter-description p {
  font-size: 0.813rem;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
}

.filter-description i {
  color: #0ea5e9;
  font-size: 0.875rem;
}

.records-container {
  background: white;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(226, 232, 240, 0.8);
  margin-bottom: 24px;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e2e8f0;
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #e0f2fe;
  color: #0ea5e9;
}

.header-icon i {
  font-size: 1.5rem;
}

.card-header h2 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
}

.filters {
  margin-bottom: 24px;
  display: flex;
  align-items: center;
}

.filter-group {
  display: flex;
  gap: 16px;
}

.subsidiary-selector {
  position: relative;
  min-width: 200px;
  z-index: 1000;
}

.selected-subsidiary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  padding: 8px 16px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(226, 232, 240, 0.8);
  cursor: pointer;
  transition: all 0.2s;
}

.selected-subsidiary:hover {
  border-color: #cbd5e1;
}

.selected-subsidiary span {
  font-size: 1rem;
  font-weight: 500;
  color: #1e293b;
}

.selected-subsidiary i {
  font-size: 0.875rem;
  color: #64748b;
  transition: transform 0.2s;
}

.selected-subsidiary i.rotate {
  transform: rotate(180deg);
}

.subsidiary-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(226, 232, 240, 0.8);
  z-index: 1001;
  max-height: 200px;
  overflow-y: auto;
}

.subsidiary-option {
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.875rem;
  color: #1e293b;
}

.subsidiary-option:hover {
  background: #f1f5f9;
}

.subsidiary-option.active {
  background: #f1f5f9;
  color: #0ea5e9;
  font-weight: 500;
}

.branch-selector {
  position: relative;
  min-width: 200px;
  z-index: 1000;
}

.branch-selector.disabled {
  opacity: 0.6;
  pointer-events: none;
}

.selected-branch {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  padding: 8px 16px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(226, 232, 240, 0.8);
  cursor: pointer;
  transition: all 0.2s;
}

.selected-branch:hover {
  border-color: #cbd5e1;
}

.selected-branch span {
  font-size: 1rem;
  font-weight: 500;
  color: #1e293b;
}

.selected-branch i {
  font-size: 0.875rem;
  color: #64748b;
  transition: transform 0.2s;
}

.selected-branch i.rotate {
  transform: rotate(180deg);
}

.branch-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(226, 232, 240, 0.8);
  z-index: 1001;
  max-height: 200px;
  overflow-y: auto;
}

.branch-option {
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.875rem;
  color: #1e293b;
}

.branch-option:hover {
  background: #f1f5f9;
}

.branch-option.active {
  background: #f1f5f9;
  color: #0ea5e9;
  font-weight: 500;
}

.month-year-select {
  display: flex;
  gap: 16px;
}

.month-year-select select {
  flex: 1;
  padding: 8px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  color: #1e293b;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  background-size: 16px;
  padding-right: 40px;
}

.month-year-select select:hover {
  border-color: #94a3b8;
}

.month-year-select select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.costs-table {
  overflow-x: auto;
}

.month-group {
  margin-bottom: 32px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

.month-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.month-header h3 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.branch-name {
  font-size: 0.875rem;
  color: #64748b;
  background: #e2e8f0;
  padding: 4px 8px;
  border-radius: 4px;
}

table {
  width: 100%;
  border-collapse: collapse;
  border-radius: 8px;
  overflow: hidden;
  background: white;
  border: 1px solid #e2e8f0;
}

th, td {
  padding: 16px 20px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.875rem;
  color: #1e293b;
}

th {
  background: #f1f5f9;
  font-weight: 600;
  color: #1e293b;
  font-size: 0.875rem;
  border-bottom: 2px solid #e2e8f0;
}

tr:hover {
  background: #f8fafc;
}

tr:last-child td {
  border-bottom: none;
}

:deep(.el-tag) {
  text-transform: capitalize;
  font-size: 0.75rem;
  padding: 0 8px;
  height: 24px;
  line-height: 22px;
}

:deep(.el-tag--success) {
  background-color: #dcfce7;
  border-color: #bbf7d0;
  color: #16a34a;
}

:deep(.el-tag--warning) {
  background-color: #fef3c7;
  border-color: #fde68a;
  color: #d97706;
}

.edit-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.edit-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.action-buttons {
  display: flex;
  gap: 8px;
  justify-content: left;
  align-items: left;
}

.edit-btn, .save-btn, .cancel-btn {
  background: none;
  border: none;
  padding: 6px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  font-size: 1rem;
}

.edit-btn {
  color: #0ea5e9;
}

.edit-btn:hover {
  background: #e0f2fe;
}

.save-btn {
  color: #16a34a;
}

.save-btn:hover {
  background: #dcfce7;
}

.cancel-btn {
  color: #ef4444;
}

.cancel-btn:hover {
  background: #fee2e2;
}

.add-record-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #0ea5e9;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.add-record-btn:hover {
  background: #0284c7;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #e2e8f0;
}

.modal-header h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  font-size: 1.25rem;
  padding: 4px;
  transition: color 0.2s;
}

.close-btn:hover {
  color: #1e293b;
}

.modal-body {
  padding: 24px;
}

.form-group {
  margin-bottom: 24px;
}

.form-group label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #1e293b;
  margin-bottom: 8px;
}

.form-group select,
.form-group input {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #1e293b;
  transition: all 0.2s;
}

.form-group select:hover,
.form-group input:hover {
  border-color: #94a3b8;
}

.form-group select:focus,
.form-group input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.modal-footer button {
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.modal-footer .cancel-btn {
  background: #f1f5f9;
  color: #64748b;
  border: 1px solid #e2e8f0;
  padding-left: 30px;
  padding-right: 30px;
}

.modal-footer .cancel-btn:hover {
  background: #e2e8f0;
}

.modal-footer .submit-btn {
  background: #0ea5e9;
  color: white;
  border: none;
}

.modal-footer .submit-btn:hover {
  background: #0284c7;
}

.no-records {
  text-align: center;
  padding: 32px;
  background: #f8fafc;
  border-radius: 8px;
  color: #64748b;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.category-section {
  background: #f8fafc;
  padding: 24px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  margin-bottom: 16px;
}

.category-section h3 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 16px;
}

.table-container {
  overflow-x: auto;
}

.input-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  table-layout: fixed;
}

.input-table th,
.input-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
}

.input-table th:first-child,
.input-table td:first-child {
  width: 50%;
}

.input-table th:last-child,
.input-table td:last-child {
  width: 50%;
}

.input-table th {
  background: #f8fafc;
  font-weight: 500;
  color: #64748b;
  font-size: 0.875rem;
}

.input-table tr:last-child td {
  border-bottom: none;
}

.input-table input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
}

.input-table input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.submit-btn {
  padding: 10px 20px;
  background: #16a34a;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 140px;
}

.submit-btn:hover:not(:disabled) {
  background: #15803d;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.submit-btn:disabled {
  background: #e2e8f0;
  color: #94a3b8;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.export-btn {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #0ea5e9;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.export-btn:hover {
  background: #0284c7;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

/* Add new styles for tabs */
.tab-navigation {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 16px;
}

.tab-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border: none;
  background: none;
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 8px;
}

.tab-button:hover {
  background: #f1f5f9;
  color: #1e293b;
}

.tab-button.active {
  background: #0ea5e9;
  color: white;
}

.tab-button i {
  font-size: 1rem;
}

.submit-container {
  background: white;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(226, 232, 240, 0.8);
}

.submission-form {
  margin-top: 24px;
}

.form-section {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 32px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
}

@media (max-width: 768px) {
  .tab-navigation {
    flex-direction: column;
    gap: 8px;
  }

  .tab-button {
    width: 100%;
    justify-content: center;
  }

  .form-section {
    grid-template-columns: 1fr;
  }
}

/* Add styles for search box */
.search-box {
  padding: 8px;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
}

.search-box input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  transition: all 0.2s;
}

.search-box input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.subsidiary-dropdown,
.branch-dropdown {
  max-height: 300px;
  overflow-y: auto;
}

.subsidiary-option,
.branch-option {
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.875rem;
  color: #1e293b;
}

.subsidiary-option:hover,
.branch-option:hover {
  background: #f1f5f9;
}

.subsidiary-option.active,
.branch-option.active {
  background: #f1f5f9;
  color: #0ea5e9;
  font-weight: 500;
}

.info-message {
  text-align: center;
  color: #64748b;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 32px 0;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.info-icon {
  color: #0ea5e9;
  font-size: 1.5rem;
  margin-bottom: 4px;
}

.error-message {
  margin-top: 8px;
  padding: 8px 12px;
  background: #fef2f2;
  border: 1px solid #fee2e2;
  border-radius: 6px;
  color: #dc2626;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
}

.error-message .info-icon {
  color: #dc2626;
  font-size: 1rem;
}

.error-message p {
  margin: 0;
}

.drag-handle {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: move;
}

.drag-handle i {
  color: #94a3b8;
  font-size: 0.875rem;
}

tr.dragging {
  opacity: 0.5;
  background: #f1f5f9;
}

tr {
  transition: background-color 0.2s ease;
}

tr:hover {
  background-color: #f8fafc;
}

.input-table tr {
  cursor: move;
}

.input-table tr td:first-child {
  user-select: none;
}

.total-input-container {
  margin: 16px 0 0 0;
  padding: 12px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.total-input-container label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #1e293b;
  margin-bottom: 8px;
}

.total-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  transition: all 0.2s;
}

.total-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.total-input:hover {
  border-color: #94a3b8;
}

.total-inputs-container {
  display: flex;
  gap: 16px;
  margin-top: 16px;
  padding: 16px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
}

.total-inputs-container .total-input-container {
  flex: 1;
  margin: 0;
}

.total-inputs-container .total-input {
  background: white;
}

.total-inputs-container .total-input[readonly] {
  background: #f1f5f9;
  cursor: not-allowed;
}

.total-input-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.total-actions {
  display: flex;
  gap: 4px;
}

.total-input-wrapper .total-input {
  flex: 1;
  background: white;
}

.total-input-wrapper .total-input[readonly] {
  background: #f1f5f9;
  cursor: not-allowed;
}

.total-input-wrapper .total-input:not([readonly]) {
  background: white;
  cursor: text;
}

.total-input-wrapper .edit-btn,
.total-input-wrapper .save-btn,
.total-input-wrapper .cancel-btn {
  padding: 4px;
  width: 28px;
  height: 28px;
}

/* Bulk entry styles */
.bulk-entry-table {
  margin-top: 24px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.table-header h3 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.copy-all-btn {
  padding: 8px 16px;
  background: #0ea5e9;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.copy-all-btn:hover {
  background: #0284c7;
}

.bulk-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.bulk-table th,
.bulk-table td {
  padding: 16px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
}

.bulk-table th {
  background: #f1f5f9;
  font-weight: 600;
  color: #1e293b;
  font-size: 0.875rem;
}

.bulk-table td {
  font-size: 0.875rem;
  color: #1e293b;
}

.bulk-table tr:last-child td {
  border-bottom: none;
}

.bulk-table tr:hover {
  background: #f8fafc;
}

.month-label {
  font-weight: 500;
  min-width: 150px;
}

.bulk-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  transition: all 0.2s;
}

.bulk-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.bulk-input:hover {
  border-color: #94a3b8;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.813rem;
  font-weight: 500;
}

.status-badge.exists {
  background: #dcfce7;
  color: #16a34a;
}

.status-badge.new {
  background: #e0f2fe;
  color: #0ea5e9;
}

.status-badge i {
  font-size: 0.875rem;
}
</style> 